import type { Deck, Page } from "@deckloom/core/schema";
import {
  applyEffectEnd,
  compileSteps,
  identityState,
  keyframesFor,
  stateAfter,
  targetKeys,
  type Step,
  type TargetKey,
  type TargetState,
} from "@deckloom/core/timeline";
import { elementBBox } from "@deckloom/core/geometry";

export interface MediaCommand {
  elementId: string;
  action: "play" | "playFrom" | "pause" | "resume" | "stop" | "togglePause";
  at?: number;
}

export interface StepPlayerOptions {
  /** receives media effects as they fire so the media layer can act on them */
  onMedia?: (cmd: MediaCommand) => void;
  /** called when a played step has finished all finite animations */
  onStepSettled?: (step: number) => void;
}

/**
 * Drives one mounted slide. All DOM writes happen on `.anim[data-el]` wrappers (and paragraph
 * nodes for text-scoped targets) inside the SVG root plus any extra roots (the HTML media layer).
 */
export class StepPlayer {
  readonly steps: Step[];
  /** click-triggered sequences (page.timeline.interactive), each compiled into its own click steps */
  private readonly interactive: { steps: Step[]; pos: number; triggerElementId: string }[];
  /** interactive steps already played on this page, in order; their resting effects stack on the main state */
  private interactiveDone: Step[] = [];
  private running: Animation[] = [];
  private timers: number[] = [];
  private current = -2; // -2 = not positioned yet
  private nodes = new Map<TargetKey, HTMLElement[]>();

  constructor(
    private readonly roots: ParentNode[],
    private readonly deck: Deck,
    private readonly page: Page,
    private readonly opts: StepPlayerOptions = {},
  ) {
    this.steps = compileSteps(page.timeline);
    this.interactive = page.timeline.interactive.map((seq) => ({
      steps: compileSteps({ main: seq.animations.map((a, i) => (i === 0 ? { ...a, trigger: "onClick" as const } : a)), interactive: [] }),
      pos: -1,
      triggerElementId: seq.triggerElementId,
    }));
    this.collectNodes();
  }

  get stepCount(): number {
    return this.steps.length;
  }

  get position(): number {
    return this.current;
  }

  /** Index of the interactive sequence a click on `elementId` (or one of its ancestors) triggers, or -1. */
  triggerFor(elementIds: string[]): number {
    for (const id of elementIds) {
      const i = this.interactive.findIndex((s) => s.triggerElementId === id);
      if (i >= 0) return i;
    }
    return -1;
  }

  private collectNodes(): void {
    this.nodes.clear();
    for (const root of this.roots) {
      for (const node of root.querySelectorAll<HTMLElement>("[data-el]")) {
        const key = node.dataset.el!;
        const list = this.nodes.get(key) ?? [];
        list.push(node);
        this.nodes.set(key, list);
      }
    }
  }

  private cancelAll(): void {
    for (const a of this.running) a.cancel();
    this.running = [];
    for (const t of this.timers) clearTimeout(t);
    this.timers = [];
  }

  private writeState(key: TargetKey, s: TargetState): void {
    const nodes = this.nodes.get(key);
    if (!nodes) return;
    for (const node of nodes) {
      node.style.visibility = s.visible ? "" : "hidden";
      node.style.opacity = s.opacity === 1 ? "" : String(s.opacity);
      const t = s.translate, sc = s.scale;
      const identity = t.x === 0 && t.y === 0 && sc.x === 1 && sc.y === 1 && s.rotate === 0;
      node.style.transform = identity ? "" : `translate(${t.x}px, ${t.y}px) rotate(${s.rotate}deg) scale(${sc.x}, ${sc.y})`;
      node.style.clipPath = "";
      if (s.fill) node.style.setProperty("--anim-fill", s.fill.hex); else node.style.removeProperty("--anim-fill");
      if (s.textColor) node.style.color = s.textColor.hex; else node.style.color = "";
    }
  }

  /** Resting state after main step `n` plus every interactive step played so far. */
  private restingState(n: number): Map<TargetKey, TargetState> {
    const state = stateAfter(this.page, this.steps, n);
    for (const step of this.interactiveDone) {
      for (const item of step.items) {
        for (const key of targetKeys(item.anim.target)) {
          const s = state.get(key) ?? identityState(true);
          applyEffectEnd(s, item.anim, this.page);
          state.set(key, s);
        }
      }
    }
    return state;
  }

  /** Jump to the resting state after step `n` (−1 = initial). Used for late joiners and `prev`. */
  seek(n: number): void {
    this.cancelAll();
    this.interactiveDone = [];
    for (const seq of this.interactive) seq.pos = -1;
    const state = stateAfter(this.page, this.steps, n);
    for (const [key, s] of state) this.writeState(key, s);
    this.current = n;
  }

  /** Play step `n`. If the player is not resting at `n-1`, it seeks there first. */
  play(n: number): void {
    if (n < 0 || n >= this.steps.length) {
      this.seek(Math.min(n, this.steps.length - 1));
      return;
    }
    if (this.current !== n - 1) this.seek(n - 1);
    this.cancelAll();
    const startState = this.restingState(n - 1);
    this.current = n;
    this.runStep(this.steps[n]!, startState, () => this.opts.onStepSettled?.(n));
  }

  /** Play the next click step of interactive sequence `seq` (wrapping around like PowerPoint). Returns false when there is none. */
  trigger(seqIndex: number): boolean {
    const seq = this.interactive[seqIndex];
    if (!seq || seq.steps.length === 0 || this.current === -2) return false;
    seq.pos = seq.pos + 1 >= seq.steps.length ? 0 : seq.pos + 1;
    const step = seq.steps[seq.pos]!;
    const startState = this.restingState(this.current);
    // a restarted sequence replays its entrances: put the targets back to their initial state first
    if (seq.pos === 0) {
      this.interactiveDone = this.interactiveDone.filter((s) => !seq.steps.includes(s));
      const fresh = this.restingState(this.current);
      for (const it of step.items) for (const key of targetKeys(it.anim.target)) {
        const s = fresh.get(key);
        if (s) { startState.set(key, s); this.writeState(key, s); }
      }
    }
    this.interactiveDone.push(step);
    this.runStep(step, startState, () => undefined);
    return true;
  }

  private runStep(step: Step, startState: Map<TargetKey, TargetState>, onSettled: () => void): void {
    /** Resting state of `key` once every effect of this step ending at or before `t` has finished. */
    const restingAt = (key: TargetKey, t: number): TargetState => {
      const base = startState.get(key) ?? identityState(true);
      const s: TargetState = { ...base, translate: { ...base.translate }, scale: { ...base.scale } };
      for (const it of step.items) {
        if (it.endMs > t || !targetKeys(it.anim.target).includes(key)) continue;
        applyEffectEnd(s, it.anim, this.page);
      }
      return s;
    };
    let pending = 0;
    const settle = () => {
      pending--;
      if (pending <= 0) onSettled();
    };

    for (const item of step.items) {
      const anim = item.anim;
      if (anim.effect.kind === "media") {
        const e = anim.effect;
        const fire = () => this.opts.onMedia?.({ elementId: anim.target.elementId, action: e.action, at: e.at });
        if (item.startMs > 0) this.timers.push(window.setTimeout(fire, item.startMs)); else fire();
        continue;
      }
      const bbox = elementBBox(this.page.elements, anim.target.elementId) ?? { x: 0, y: 0, w: this.deck.size.w, h: this.deck.size.h };
      for (const key of targetKeys(anim.target)) {
        const nodes = this.nodes.get(key);
        if (!nodes) continue;
        const s = startState.get(key) ?? identityState(true);
        const specs = keyframesFor(anim, { bbox, slide: this.deck.size, startState: s });
        for (const spec of specs) {
          for (const node of nodes) {
            if (spec.showFirst) {
              node.style.visibility = "";
              // make sure the first keyframe is in place before the delay elapses
              const first = spec.keyframes[0] ?? {};
              if (first.opacity !== undefined) node.style.opacity = String(first.opacity);
              if (first.transform !== undefined) node.style.transform = String(first.transform);
              if (first.clipPath !== undefined) node.style.clipPath = String(first.clipPath);
            }
            const a = node.animate(spec.keyframes as Keyframe[], {
              duration: spec.options.duration,
              delay: item.startMs + spec.options.delay,
              easing: spec.options.easing,
              iterations: spec.options.iterations,
              direction: spec.options.direction,
              fill: spec.options.fill,
            });
            this.running.push(a);
            pending++;
            a.finished
              .then(() => {
                // commitStyles() is unreliable on SVG nodes, so write the computed resting state ourselves.
                const resting = anim.rewindAfter ? s : restingAt(key, item.endMs);
                this.writeState(key, resting);
                a.cancel();
                this.running = this.running.filter((x) => x !== a);
                if (spec.hideAfter) node.style.visibility = "hidden";
                settle();
              })
              .catch(() => { /* cancelled */ });
          }
        }
      }
    }
    if (pending === 0) onSettled();
  }

  /** Enter the page: seek to the initial state, then play the automatic step 0 when there is one. */
  enter(): number {
    this.seek(-1);
    if (this.steps[0]?.auto) {
      this.play(0);
      return 0;
    }
    return -1;
  }

  destroy(): void {
    this.cancelAll();
  }
}
